<template>
  <div :key="provider" class="agreement-records-page">
    <a-card :bordered="false" :title="`${providerConfig.title}日趋势`" class="section-card">
      <template #extra><span class="range-label">{{ formatRange(chartRange) }}</span></template>
      <RecordDateFilter :model-value="chartFilter" v-model:quick-value="chartQuickDate" :loading="chartLoading" :allow-clear="false"
        @update:model-value="Object.assign(chartFilter, $event)" @quick-change="handleChartQuickDate" @search="searchChart" @reset="resetChart" />
      <p class="metric-note">按记录创建日期统计；签约记录数为当前已签约的记录。</p>
      <a-spin :spinning="chartLoading">
        <a-result v-if="chartError" status="warning" title="日趋势加载失败" :sub-title="chartError">
          <template #extra><a-button type="primary" @click="fetchChart(chartRange)">重新加载</a-button></template>
        </a-result>
        <template v-else-if="chartRows.length">
          <v-chart :option="chartOption" autoresize class="trend-chart" role="img" :aria-label="chartAriaLabel" />
          <div class="chart-summary" aria-live="polite">
            <span>范围内总记录数：<b class="total-value">{{ formatCount(chartSummary.totalCount) }}</b> 条</span>
            <span>范围内签约记录数：<b class="agreement-value">{{ formatCount(chartSummary.agreementCount) }}</b> 条</span>
          </div>
        </template>
        <a-empty v-else :description="chartLoading ? '正在加载日趋势' : '当前日期范围暂无记录'" class="chart-empty" />
      </a-spin>
    </a-card>

    <a-card :bordered="false" :title="`${providerConfig.title}签约记录`" class="section-card">
      <template #extra><span class="range-label">{{ formatRange(listRange) }}</span></template>
      <RecordDateFilter :model-value="listFilter" v-model:quick-value="listQuickDate" :loading="listLoading"
        @update:model-value="Object.assign(listFilter, $event)" @quick-change="handleListQuickDate" @search="searchRecords" @reset="resetRecords" />
      <p class="metric-note">日期留空查询全部订单，按 ID 倒序；{{ providerConfig.amountNote }}展开记录可查看商品、商户及签约信息。</p>
      <a-result v-if="listError" status="warning" title="签约记录加载失败" :sub-title="listError">
        <template #extra>
          <a-button type="primary" :loading="listLoading" @click="fetchRecords(pagination.current, pagination.pageSize)">重新加载</a-button>
        </template>
      </a-result>
      <a-table v-else :columns="columns" :data-source="records" :loading="listLoading" :pagination="pagination"
        row-key="id" :scroll="{ x: tableWidth }" size="small" bordered @change="handleTableChange">
        <template #bodyCell="{ column, record, text }">
          <template v-if="column.boolean">
            <a-tag :color="record[column.key] === true ? 'green' : 'default'">{{ formatBoolean(record[column.key]) }}</a-tag>
          </template>
          <template v-else-if="column.key === 'mode'">{{ formatMode(record.mode) }}</template>
          <template v-else-if="column.amount">
            {{ formatMinorAmount(record[column.key], record.currency) }}
          </template>
          <template v-else>{{ formatText(text) }}</template>
        </template>
        <template #expandedRowRender="{ record }">
          <a-descriptions class="record-details" size="small" bordered :column="{ xs: 1, sm: 2, lg: 3 }">
            <a-descriptions-item v-for="field in detailFields" :key="field.key" :label="field.label"
              :span="field.wide ? 3 : 1">
              <pre v-if="field.key === 'metaAttribution'" class="attribution-text">{{ formatAttribution(record.metaAttribution) }}</pre>
              <span v-else class="detail-value">{{ formatDetail(record, field) }}</span>
            </a-descriptions-item>
          </a-descriptions>
        </template>
        <template #emptyText><a-empty description="当前日期范围暂无签约记录" /></template>
      </a-table>
    </a-card>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, reactive, ref, watch } from "vue";
import { message } from "ant-design-vue";
import VChart from "vue-echarts";
import RecordDateFilter from "@/components/RecordDateFilter.vue";
import { getQuickRange, formatRecordRange, getDateRangeError } from "@/utils/recordDates";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { LineChart } from "echarts/charts";
import { AriaComponent, GridComponent, LegendComponent, TooltipComponent } from "echarts/components";
import { analyticsUrl } from "@/utils/api";
import { get } from "@/utils/request";
import { agreementProviders } from "@/utils/agreementProviders";

const props = defineProps({
  provider: { type: String, required: true, validator: (value) => Object.hasOwn(agreementProviders, value) },
});
const providerConfig = computed(() => agreementProviders[props.provider]);

use([CanvasRenderer, LineChart, AriaComponent, GridComponent, LegendComponent, TooltipComponent]);

const chartFilter = reactive(getQuickRange("last_30_days"));
const getEmptyListRange = () => ({ startDate: "", endDate: "" });
const listFilter = reactive(getEmptyListRange());
const chartRange = ref({ ...chartFilter });
const listRange = ref({ ...listFilter });
const chartQuickDate = ref("last_30_days");
const listQuickDate = ref(null);
const chartLoading = ref(false);
const listLoading = ref(false);
const chartError = ref("");
const listError = ref("");
const chartRows = ref([]);
const chartSummary = ref({ totalCount: 0, agreementCount: 0 });
const records = ref([]);
let chartRequestSequence = 0;
let listRequestSequence = 0;

const pagination = reactive({
  current: 1,
  pageSize: 50,
  total: 0,
  showSizeChanger: true,
  pageSizeOptions: ["20", "50", "100", "200"],
  showQuickJumper: true,
  showTotal: (total) => `共 ${Number(total).toLocaleString("zh-CN")} 条`,
});

const formatText = (value) => value === null || value === undefined || value === "" ? "-" : String(value);
const formatBoolean = (value) => value === true ? "是" : value === false ? "否" : "-";
const formatMode = (value) => ({ independent: "独立签约", payment: "支付并签约" }[value] || formatText(value));
const formatCount = (value) => Number(value || 0).toLocaleString("zh-CN");
const formatMinorAmount = (value, currency) => {
  if (value === null || value === undefined || value === "" || !Number.isFinite(Number(value))) return "-";
  const amount = (Number(value) / 100).toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return props.provider === "airwallex" ? `${amount} ${currency || "币种未提供"}` : `¥${amount}`;
};
const formatRange = (range) => formatRecordRange(range, "全部订单");
const formatAttribution = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  try { return JSON.stringify(typeof value === "string" ? JSON.parse(value) : value, null, 2); }
  catch { return String(value); }
};
const formatDetail = (record, field) => {
  if (field.boolean) return formatBoolean(record[field.key]);
  return formatText(record[field.key]);
};

const validateRange = (range, allowOpenRange = false) => {
  const error = getDateRangeError(range, allowOpenRange);
  if (error) message.warning(error);
  return !error;
};

const nowrap = () => ({ style: { whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" } });
const columns = computed(() => providerConfig.value.columns.map((column) => ({ ...column, customCell: nowrap })));
const detailFields = computed(() => providerConfig.value.detailFields);
const tableWidth = computed(() => columns.value.reduce((width, column) => width + column.width, 60));

const chartAriaLabel = computed(() => `${formatRange(chartRange.value)}，${providerConfig.value.title}总记录数 ${formatCount(chartSummary.value.totalCount)} 条，当前已签约记录数 ${formatCount(chartSummary.value.agreementCount)} 条。`);
const chartOption = computed(() => ({
  animation: false,
  aria: { enabled: true, description: chartAriaLabel.value },
  color: ["#1677ff", "#722ed1"],
  grid: { left: 16, right: 24, top: 48, bottom: 24, containLabel: true },
  legend: { top: 4, data: ["总记录数", "签约记录数"] },
  tooltip: { trigger: "axis", confine: true },
  xAxis: {
    type: "category", boundaryGap: false, data: chartRows.value.map((row) => row.statDate),
    axisLabel: { formatter: (value) => value.slice(5), hideOverlap: true },
  },
  yAxis: { type: "value", name: "记录数（条）", min: 0, minInterval: 1 },
  series: [
    { name: "总记录数", type: "line", smooth: 0.35, smoothMonotone: "x", symbol: "circle", symbolSize: 6, lineStyle: { cap: "round", join: "round" }, data: chartRows.value.map((row) => row.totalCount) },
    { name: "签约记录数", type: "line", smooth: 0.35, smoothMonotone: "x", symbol: "diamond", symbolSize: 7, lineStyle: { type: "dashed", cap: "round", join: "round" }, data: chartRows.value.map((row) => row.agreementCount) },
  ],
}));

const fetchChart = async (range = chartRange.value) => {
  const requestRange = { startDate: range.startDate, endDate: range.endDate };
  if (!validateRange(requestRange)) return;
  const sequence = ++chartRequestSequence;
  chartRange.value = requestRange;
  chartLoading.value = true;
  chartError.value = "";
  chartRows.value = [];
  chartSummary.value = { totalCount: 0, agreementCount: 0 };
  try {
    const json = await get(analyticsUrl(providerConfig.value.statsPath), requestRange);
    if (sequence !== chartRequestSequence) return;
    if (json.code !== 200 || !Array.isArray(json.data?.data)) throw new Error(json.content || "日趋势数据格式错误");
    chartRows.value = json.data.data.map((row) => ({
      statDate: String(row.statDate || ""), totalCount: Number(row.totalCount || 0), agreementCount: Number(row.agreementCount || 0),
    })).sort((left, right) => left.statDate.localeCompare(right.statDate));
    chartSummary.value = {
      totalCount: Number(json.data.summary?.totalCount || 0),
      agreementCount: Number(json.data.summary?.agreementCount || 0),
    };
  } catch (error) {
    if (sequence === chartRequestSequence) chartError.value = error?.message || "请求失败，请稍后重试";
  } finally {
    if (sequence === chartRequestSequence) chartLoading.value = false;
  }
};

const fetchRecords = async (pageIdx = 1, pageSize = pagination.pageSize, range = listRange.value) => {
  const requestRange = { startDate: range.startDate || "", endDate: range.endDate || "" };
  if (!validateRange(requestRange, true)) return;
  const sequence = ++listRequestSequence;
  listRange.value = requestRange;
  pagination.current = pageIdx;
  pagination.pageSize = pageSize;
  listLoading.value = true;
  listError.value = "";
  records.value = [];
  try {
    const json = await get(analyticsUrl(providerConfig.value.listPath), { ...requestRange, pageIdx, pageSize });
    if (sequence !== listRequestSequence) return;
    if (json.code !== 200 || !Array.isArray(json.data?.data)) throw new Error(json.content || "签约记录数据格式错误");
    records.value = json.data.data;
    pagination.current = Number(json.data.pageIdx || pageIdx);
    pagination.pageSize = Number(json.data.pageSize || pageSize);
    pagination.total = Number(json.data.dataSum || 0);
  } catch (error) {
    if (sequence === listRequestSequence) {
      listError.value = error?.message || "请求失败，请稍后重试";
      pagination.total = 0;
    }
  } finally {
    if (sequence === listRequestSequence) listLoading.value = false;
  }
};

const searchChart = () => fetchChart(chartFilter);
const searchRecords = () => fetchRecords(1, pagination.pageSize, listFilter);
const handleChartQuickDate = ({ target }) => {
  const range = getQuickRange(target.value);
  if (!range) return;
  Object.assign(chartFilter, range);
  return searchChart();
};
const handleListQuickDate = ({ target }) => {
  const range = getQuickRange(target.value);
  if (!range) return;
  Object.assign(listFilter, range);
  return searchRecords();
};
const resetChart = () => {
  chartQuickDate.value = "last_30_days";
  return handleChartQuickDate({ target: { value: chartQuickDate.value } });
};
const resetRecords = () => {
  listQuickDate.value = null;
  Object.assign(listFilter, getEmptyListRange());
  return searchRecords();
};
const handleTableChange = (page) => fetchRecords(page.pageSize === pagination.pageSize ? page.current : 1, page.pageSize);

const resetProvider = () => {
  // Invalidate both queues before exposing the next provider's data and filters.
  chartRequestSequence += 1;
  listRequestSequence += 1;
  Object.assign(chartFilter, getQuickRange("last_30_days"));
  Object.assign(listFilter, getEmptyListRange());
  chartQuickDate.value = "last_30_days";
  listQuickDate.value = null;
  pagination.current = 1;
  pagination.pageSize = 50;
  pagination.total = 0;
  fetchChart(chartFilter);
  fetchRecords(1, pagination.pageSize, listFilter);
};
watch(() => props.provider, resetProvider, { flush: "sync" });
onMounted(() => { fetchChart(); fetchRecords(); });
onUnmounted(() => { chartRequestSequence += 1; listRequestSequence += 1; });
</script>

<style scoped>
.agreement-records-page { min-width: 0; }
.section-card + .section-card { margin-top: 16px; }
.metric-note { margin: 8px 0 20px; color: rgba(0, 0, 0, 0.65); font-size: 13px; line-height: 1.7; }
.range-label { color: rgba(0, 0, 0, 0.65); font-size: 13px; font-variant-numeric: tabular-nums; }
.trend-chart, .chart-empty { height: 360px; }
.chart-empty { display: flex; flex-direction: column; justify-content: center; }
.chart-summary { display: flex; flex-wrap: wrap; gap: 12px 32px; border-top: 1px solid #f0f0f0; padding-top: 16px; font-variant-numeric: tabular-nums; }
.total-value { color: #1677ff; }
.agreement-value { color: #722ed1; }
.record-details { max-width: 100%; }
.detail-value, .attribution-text { white-space: pre-wrap; overflow-wrap: anywhere; word-break: break-word; }
.attribution-text { margin: 0; font-size: 12px; max-height: 280px; overflow-y: auto; }
@media (max-width: 768px) {
  .agreement-records-page :deep(.ant-card-body) { padding: 16px; }
  .agreement-records-page :deep(.ant-card-head-wrapper) { align-items: flex-start; flex-wrap: wrap; gap: 4px; padding-block: 12px; }
  .agreement-records-page :deep(.ant-card-extra) { margin-inline-start: 0; }
  .trend-chart { height: 300px; }
}
</style>
